import React, { useEffect, useState } from 'react';
import { AlertOctagon, ArrowRight, Info } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ResolvedPeriod } from '../PeriodPicker';
import { EscapeTaxonomyTableResponse, QualityApi } from './qualityApi';

interface Props {
  resolvedPeriod: ResolvedPeriod | null;
}

/**
 * Aggregated table for the Escape root-cause classifier. Requires
 * `quality.escape_root_cause_field_id` to be set in Quality settings; when
 * unset the component shows a configuration hint instead of an error.
 */
export const EscapeRootCauseTable: React.FC<Props> = ({ resolvedPeriod }) => {
  const [data, setData] = useState<EscapeTaxonomyTableResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await QualityApi.getEscapeTaxonomyTable({ period: null, resolvedPeriod });
        if (!cancelled) setData(res);
      } catch (err) {
        if (!cancelled) {
          setData(null);
          setError(err instanceof Error ? err.message : 'Failed to load escape taxonomy');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedPeriod?.startDate, resolvedPeriod?.endDate]);

  return (
    <div className="chart-card quality-escape-table">
      <div className="chart-header">
        <h3 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <AlertOctagon size={18} />
          Escape root-cause breakdown
          <span
            className="stat-hint-icon"
            title="Which root causes are producing escaped defects. Configure the Jira field in Settings → Quality → Escape root-cause."
            aria-label="About Escape root-cause"
          >
            <Info size={14} />
          </span>
        </h3>
      </div>
      {loading ? (
        <p className="quality-breakdown__empty">Loading…</p>
      ) : error ? (
        <p className="quality-breakdown__empty">Failed to load: {error}</p>
      ) : !data?.configured ? (
        <div className="quality-breakdown__empty dynamics-empty-diagnostic">
          <p>
            <strong>Escape root-cause field is not configured.</strong>
          </p>
          <p>
            Pick the Jira custom field that classifies why each escaped bug slipped through
            (regression / missed requirement / environment / data / flaky).
          </p>
          <p style={{ marginTop: 10 }}>
            <Link
              to="/settings?tab=project&section=quality-settings"
              className="btn btn-primary"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '8px 14px',
                borderRadius: 6,
                textDecoration: 'none',
              }}
            >
              Set up in Settings → Quality
              <ArrowRight size={14} />
            </Link>
          </p>
        </div>
      ) : data.rows.length === 0 ? (
        <p className="quality-breakdown__empty">No bugs with a root-cause value in this Period.</p>
      ) : (
        <table className="estimation-table">
          <thead>
            <tr>
              <th>Root cause</th>
              <th>Bugs</th>
              <th>% of total</th>
              <th>Avg MTTR (h)</th>
              <th>Top component</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map((r) => (
              <tr key={r.cause}>
                <td>{r.cause}</td>
                <td>{r.count}</td>
                <td>{r.percentOfTotal}%</td>
                <td>{r.avgMttrHours != null ? r.avgMttrHours : '—'}</td>
                <td>{r.topComponent || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};
